import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import {
  LARGE_TRADES_SEARCH_AVAILABLE,
  LARGE_TRADES_SORT_AVAILABLE,
} from '@/features/brokers/api/brokerListControls'
import { useLargeTradeLeaders, useLargeTrades } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { resolveViewState } from '@/lib/view-state'
import { pageSizeSchema } from '@/schemas/table'
import {
  LARGE_TRADE_SORTS,
  largeTradesSearchSchema,
  toLargeTradeLeadersQuery,
  toLargeTradesQuery,
  toLargeTradesTodayQuery,
  type LargeTradeSort,
} from '@/screens/brokers/search'
import { asLoadState } from '@/screens/brokers/components/broker/broker-format'
import BrokerLeadersCard from '@/screens/brokers/components/large-trades/BrokerLeadersCard'
import Filters from '@/screens/brokers/components/large-trades/Filters'
import {
  buildBrokerOptions,
  buildStockOptions,
  withSelected,
} from '@/screens/brokers/components/large-trades/filterOptions'
import SummaryBandSkeleton from '@/screens/brokers/components/large-trades/SummaryBandSkeleton'
import SummaryStrip from '@/screens/brokers/components/large-trades/SummaryStrip'
import TradesCard from '@/screens/brokers/components/large-trades/TradesCard'
import { loadLargeTrades } from '@/screens/brokers/LargeTradesScreen.loader'

const HEADER = <TitleRow title="Large Trades" />

/** `Filters`' own row: the search on the left, the two selects on the right. */
function FiltersSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Skeleton className="h-11 w-full rounded-md @sm:w-60 lg:h-9" />
      <div className="flex flex-wrap items-center gap-3">
        {Array.from({ length: 2 }, (_, index) => (
          <Skeleton key={index} className="h-11 w-full rounded-sm @sm:w-28 lg:h-9" />
        ))}
      </div>
    </div>
  )
}

/** `largeTradesQuerySchema`'s own `pageSize` default (10, the mock's) is the row count; a
 *  `DataTable` at its default one-line height. Seven columns. */
const TABLE_ROWS = 10

/** `BrokerLeadersCard`'s heading row and its table of seven two-line rows. */
function BrokerLeadersSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-h-8 items-center">
        <Skeleton className="h-4 w-32 rounded-xs" />
      </div>
      <DataTableSkeleton rows={7} columns={2} rowHeight="lg" />
    </div>
  )
}

function LargeTradesPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading large trades…"
      header={HEADER}
      pageTitle="Large Trades"
    >
      <div className="flex flex-col gap-6">
        <FiltersSkeleton />
        <div>
          <DataTableSkeleton rows={TABLE_ROWS} columns={7} />
          {/* `Pagination`'s own row, 24 under the table. */}
          <div className="mt-6 flex h-8 items-center justify-between gap-3">
            <Skeleton className="h-4 w-32 rounded-xs" />
            <Skeleton className="h-8 w-56 rounded-sm" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-8">
        <SummaryBandSkeleton />
        <BrokerLeadersSkeleton />
      </div>
    </ScreenLayout>
  )
}

function LargeTradesError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Large trades did not load" />
}

export const Route = createFileRoute('/_app/brokers/large-trades')({
  validateSearch: largeTradesSearchSchema,
  loader: loadLargeTrades,
  pendingComponent: LargeTradesPending,
  errorComponent: LargeTradesError,
  component: LargeTradesScreen,
})

/**
 * Brokers · Large trades.
 *
 * `validateSearch` is `largeTradesQuerySchema` minus `size` (the mock fixes the floor at Rs 10 L
 * and draws no Size control) plus the table's `sort` / `dir` (see search.ts). `Route.useSearch()`
 * still goes through `toLargeTradesQuery` before reaching `useLargeTrades`, the same picker the
 * loader uses.
 *
 * Broker Analysis mock parity (2026-10-28): the body runs the filter row (search, All stocks, All
 * brokers), the trades table with its pager and its row card (hover or focus shows it, a click
 * pins it), ONE summary band, then Broker leaders. The band and the leaders are TODAY's, never the
 * table's filters (owner ruling 4): the band is its own unfiltered read, which also feeds the two
 * menus' options; a leader's row picks its broker into the table and scrolls up to it.
 *
 * Sort and search are drawn but cannot run (`brokerListControls.ts`): the endpoint ranks
 * biggest-first and takes no `order` or `q`. The head shows Amount, high to low, the order served,
 * and any other pick (Time, the mock's newest-first, included) leaves the head and the field in
 * place over "Not available yet".
 *
 * No `period`: large trades reads today's raw floorsheet only (`services/broker/largetrades.py`),
 * and `LargeTrades` carries no `window` object — there is no partial-window caption to draw here,
 * unlike every other Broker Chirfaar surface.
 */
function LargeTradesScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { page, pageSize, stock, broker } = search

  const [stockOpen, setStockOpen] = useState(false)
  const [brokerOpen, setBrokerOpen] = useState(false)
  const [query, setQuery] = useState('')

  const online = useOnline()
  const trades = useLargeTrades(toLargeTradesQuery(search))
  // Today's whole read at the floor: the band's numbers and the menus' options, whatever the table
  // is filtered to. The same cache entry as `trades` on the unfiltered first page of 100.
  const today = useLargeTrades(toLargeTradesTodayQuery())
  const leaders = useLargeTradeLeaders(toLargeTradeLeadersQuery())
  // `data === null` is `nullOnNoFloorsheetData`'s answer for `NO_FLOORSHEET_DATA`
  // (queries.ts) — no floorsheet has posted yet today, not a failure.
  // `asLoadState` folds that into `'loaded'`; `notSettledYet` below is what
  // tells `TradesCard` to draw "No floorsheet yet today" instead of the
  // ordinary empty table.
  const notSettledYet = trades.data === null
  // A failed background refetch keeps whatever page of trades is already on
  // screen; only a data-less query reads as failed/offline (CRS-ST-2).
  const tradesView = asLoadState(resolveViewState(trades, { isEmpty: (d) => d === null, online }))

  /**
   * The trades query keys on the filters and carries `keepPreviousData`, so it holds `isLoading`
   * FALSE while a stock or broker change is in flight — a member narrowing to one broker kept the
   * previous rows on screen, unmarked, until the new payload landed. The band and the leaders do
   * not follow the filters, so only the table is marked.
   */
  const isUpdating = trades.isPlaceholderData

  // Neither endpoint this screen may call enumerates the day's full stock or broker universe (see
  // filterOptions.ts) — the two menus are built from today's biggest 100 trades and the
  // leaderboard, with the current selection kept as an option even once it falls out of that data.
  const stockOptions = useMemo(
    () =>
      withSelected(
        buildStockOptions([...(today.data?.trades ?? []), ...(trades.data?.trades ?? [])]),
        stock,
        (value) => value,
      ),
    [today.data, trades.data, stock],
  )
  const brokerOptions = useMemo(
    () =>
      withSelected(
        buildBrokerOptions(leaders.data ?? [], [
          ...(today.data?.trades ?? []),
          ...(trades.data?.trades ?? []),
        ]),
        broker,
        (value) => `Broker ${value}`,
      ),
    [leaders.data, today.data, trades.data, broker],
  )

  const stockLabel =
    stock === undefined
      ? 'All stocks'
      : (stockOptions.find((option) => option.value === stock)?.label ?? stock)
  const brokerLabel =
    broker === undefined
      ? 'All brokers'
      : (brokerOptions.find((option) => option.value === broker)?.label ?? `Broker ${broker}`)

  /** The head the table is sorted by: the served order (Amount, high to low) when none is named. */
  const sortBy: LargeTradeSort = search.sort ?? 'amount'
  const sortDesc = search.sort === undefined || search.dir !== 'asc'
  const onSortChange = (next: string | undefined, desc: boolean) => {
    const sort = LARGE_TRADE_SORTS.find((candidate) => candidate === next)
    void navigate({
      search: (prev) => ({
        ...prev,
        sort,
        dir: sort === undefined ? undefined : desc ? 'desc' : 'asc',
        page: 1,
      }),
    })
  }

  // What the table cannot do yet (brokerListControls.ts): the served order is Amount, high to low,
  // and nothing searches free text.
  const searching = query.trim() !== ''
  const servedOrder = sortBy === 'amount' && sortDesc
  const unavailable =
    searching && !LARGE_TRADES_SEARCH_AVAILABLE
      ? 'Searching large trades by stock or broker'
      : !servedOrder && !LARGE_TRADES_SORT_AVAILABLE
        ? 'Sorting large trades by this column'
        : null

  // A leader's row picks its broker (it is a link that sets `?broker=`); the table scrolls into
  // view once the pick lands, as the mock's does.
  const tableRef = useRef<HTMLDivElement>(null)
  const pickedFromLeaders = useRef(false)
  useEffect(() => {
    if (!pickedFromLeaders.current) return
    pickedFromLeaders.current = false
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    tableRef.current?.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' })
  }, [broker])

  return (
    <ScreenLayout header={HEADER} pageTitle="Large Trades">
      {/* Filters and table sit 24 apart (the mock); the pager keeps its own 24 under the table.
          The table is kept as it is, never blanked, while a filter loads: it holds real rows the
          member may still be reading. Routing `isPlaceholderData` into `isLoading` instead would
          put a skeleton back on screen on every filter click. */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Filters
            query={query}
            onQueryChange={setQuery}
            stock={stock}
            stockLabel={stockLabel}
            stockOptions={stockOptions}
            stockOpen={stockOpen}
            onStockOpenChange={setStockOpen}
            onStockChange={(next) => {
              void navigate({
                search: (prev) => ({ ...prev, stock: next === 'all' ? undefined : next, page: 1 }),
              })
            }}
            broker={broker}
            brokerLabel={brokerLabel}
            brokerOptions={brokerOptions}
            brokerOpen={brokerOpen}
            onBrokerOpenChange={setBrokerOpen}
            onBrokerChange={(next) => {
              void navigate({
                search: (prev) => ({ ...prev, broker: next === 'all' ? undefined : next, page: 1 }),
              })
            }}
          />
          <UpdatingNote when={isUpdating} label="Updating these filters…" />
        </div>

        <div ref={tableRef} className="scroll-mt-4">
          <RefreshRegion updating={isUpdating}>
            <TradesCard
              data={trades.data ?? undefined}
              view={tradesView}
              notSettledYet={notSettledYet}
              onRetry={() => void trades.refetch()}
              retrying={trades.isFetching}
              savedAt={trades.dataUpdatedAt}
              page={page}
              pageSize={pageSize}
              onPageChange={(next) => {
                void navigate({ search: (prev) => ({ ...prev, page: next }) })
              }}
              onPageSizeChange={(next) => {
                void navigate({
                  search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(next), page: 1 }),
                })
              }}
              sortBy={sortBy}
              sortDesc={sortDesc}
              onSortChange={onSortChange}
              broker={broker}
              filtered={stock !== undefined || broker !== undefined || searching}
              onClearFilters={() => {
                setQuery('')
                void navigate({
                  search: (prev) => ({ ...prev, stock: undefined, broker: undefined, page: 1 }),
                })
              }}
              unavailable={unavailable}
            />
          </RefreshRegion>
        </div>
      </div>

      {/* Today's, not the table's: neither follows the stock or broker filter. No floorsheet yet
          today means no band (the table says so). */}
      <div className="flex flex-col gap-8">
        {today.data === null ? null : (
          <SummaryStrip data={today.data} isLoading={today.isLoading} />
        )}
        <BrokerLeadersCard
          leaders={leaders.data}
          isLoading={leaders.isLoading}
          isError={leaders.isError}
          onRetry={() => void leaders.refetch()}
          retrying={leaders.isFetching}
          picked={broker}
          pickSearch={(code) => ({
            broker: code,
            page: 1,
            pageSize,
            ...(stock === undefined ? {} : { stock }),
            ...(search.sort === undefined ? {} : { sort: search.sort }),
            ...(search.dir === undefined ? {} : { dir: search.dir }),
          })}
          onPick={() => {
            pickedFromLeaders.current = true
          }}
        />
      </div>
    </ScreenLayout>
  )
}
